import { useContext, useCallback, useMemo } from "react";
import { useThemeContext, ThemeContext, ThemeContextValue } from "./ThemeContext";
import { lightThemeColors } from "./tokens/lightTheme";

/**
 * Primary hook for consuming the centralized Lighthouse theme system.
 *
 * Exposes the user's raw preference, the resolved 'light' | 'dark' theme
 * (with 'system' already resolved against the OS color scheme), semantic
 * color tokens, and a method to change the preference.
 *
 * Should be called from a component rendered inside `<ThemeProvider>`.
 * If used outside of ThemeProvider, returns a safe default (light theme).
 *
 * @example
 * ```tsx
 * const { colors, resolvedTheme, preference, setThemePreference } = useTheme();
 *
 * const styles = StyleSheet.create({
 *   container: { backgroundColor: colors.background.page },
 * });
 * ```
 */
export const useTheme = (): ThemeContextValue => {
  // Try to get the context value
  const context = useContext(ThemeContext);
  
  // If we have the context, return it
  if (context) {
    return context;
  }
  
  // Provide a safe default fallback (light theme) for rendering outside of ThemeProvider
  // This prevents the "must be used within" error during component rendering/testing
  const defaultValue = useMemo<ThemeContextValue>(
    () => ({
      preference: "light",
      resolvedTheme: "light",
      colors: lightThemeColors,
      setThemePreference: () => {
        console.warn("setThemePreference called outside of ThemeProvider context");
      },
    }),
    []
  );
  
  return defaultValue;
};
